import { createSlice } from "@reduxjs/toolkit";
import { REPORTING } from "modules/reporting/constants";
import { getCreditMovementListing, getProductSkuList } from "../action";
import moment from "moment";

// Helper function to format date to user timezone
const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    // Parse as UTC then convert to local timezone
    return moment.utc(dateString).local().format("DD MMM YYYY, HH:mm");
};

// Default date range to 2 weeks before until today
const getDefaultDateRange = () => ({
    from: moment().subtract(2, 'weeks').format("YYYY-MM-DD"),
    to: moment().format("YYYY-MM-DD"),
});

const initialState = {
    // Listing data
    listingData: {
        isLoading: false,
        isSuccess: false,
        noResult: false,
        data: [],
        totalRecords: 0,
        totalPages: 0,
    },
    // Pagination
    pagination: {
        page: 0,
        rowsPerPage: 25,
    },
    // Filters
    filters: {
        searchPrintJobId: "",
        dateRange: getDefaultDateRange(),
        productSku: null,
    },
    // Filter options (dropdowns)
    filterOptions: {
        products: [],
    },
};

const reportingCreditMovementSlice = createSlice({
    name: REPORTING,
    initialState,
    reducers: {
        // Initialize
        initCreditMovement: (state) => {
            state.listingData = initialState.listingData;
            state.pagination = initialState.pagination;
        },
        // Set listing data
        setCreditMovementListingData: (state, action) => {
            state.listingData = {
                ...state.listingData,
                ...action.payload,
            };
        },
        setCreditMovementLoading: (state, action) => {
            state.listingData.isLoading = action.payload;
        },
        // Pagination
        setCreditMovementPage: (state, action) => {
            state.pagination.page = action.payload;
        },
        setCreditMovementRowsPerPage: (state, action) => {
            state.pagination.rowsPerPage = action.payload;
            state.pagination.page = 0;
        },
        // Filters
        setCreditMovementFilters: (state, action) => {
            state.filters = {
                ...state.filters,
                ...action.payload,
            };
        },
        resetCreditMovementFilters: (state) => {
            state.filters = {
                searchPrintJobId: "",
                dateRange: getDefaultDateRange(),
                productSku: null,
            };
            state.pagination.page = 0;
        },
        setSearchPrintJobId: (state, action) => {
            state.filters.searchPrintJobId = action.payload;
        },
        // Filter options
        setFilterOptions: (state, action) => {
            state.filterOptions = {
                ...state.filterOptions,
                ...action.payload,
            };
        },
    },
    extraReducers: (builder) => {
        builder
            // Get Credit Movement Listing
            .addCase(getCreditMovementListing.pending, (state) => {
                state.listingData = {
                    ...state.listingData,
                    isLoading: true,
                };
            })
            .addCase(getCreditMovementListing.fulfilled, (state, action) => {
                const data = action.payload.data;
                const records = data?.records || [];
                const totalRecords = data?.totalRecords || 0;
                const rowsPerPage = state.pagination.rowsPerPage;

                state.listingData = {
                    isLoading: false,
                    isSuccess: action.payload.success,
                    noResult: records.length === 0,
                    data: records.map((item) => ({
                        printJobId: item.print_job_id,
                        productSku: item.product_sku,
                        startDateTime: formatDateTime(item.start_date),
                        completionDateTime: formatDateTime(item.completion_date),
                        balanceAfter: Math.floor(item.balance_after || 0),
                        requested: item.requested,
                        printed: item.printed,
                        reimbursed: item.reimbursed,
                    })),
                    totalRecords: totalRecords,
                    totalPages: Math.ceil(totalRecords / rowsPerPage),
                };
            })
            .addCase(getCreditMovementListing.rejected, (state) => {
                state.listingData = {
                    ...initialState.listingData,
                    isSuccess: false,
                    noResult: true,
                    isLoading: false,
                };
            })
            // Get Product SKU List
            .addCase(getProductSkuList.fulfilled, (state, action) => {
                const data = action.payload.data;
                const productSkuList = data?.product_sku || [];

                state.filterOptions.products = productSkuList.map((sku) => ({
                    value: sku,
                    label: sku,
                }));
            });
    },
});

export const {
    initCreditMovement,
    setCreditMovementListingData,
    setCreditMovementLoading,
    setCreditMovementPage,
    setCreditMovementRowsPerPage,
    setCreditMovementFilters,
    resetCreditMovementFilters,
    setSearchPrintJobId,
    setFilterOptions,
} = reportingCreditMovementSlice.actions;

export const reportingCreditMovementReducer = reportingCreditMovementSlice.reducer;
